import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import type { CorrectionTagT, ProfileClaim, ProfileResponse } from '@resonance/shared-types';
import { api } from '~/lib/api';
import { Button } from '~/components/ui/button';
import { Textarea } from '~/components/ui/input';
import { Badge } from '~/components/ui/badge';
import { Page, PageHeader, Section, EmptyState } from '~/components/ui/page';

export const Route = createFileRoute('/_authed/profile')({
  component: ProfilePage,
});

const TAGS: Array<{ id: CorrectionTagT; label: string }> = [
  { id: 'too_strong', label: 'too strong' },
  { id: 'wrong_shape', label: 'wrong shape' },
  { id: 'right_idea_wrong_words', label: 'right idea, wrong words' },
  { id: 'completely_wrong', label: 'completely wrong' },
];

function ClaimRow(props: { claim: ProfileClaim; onCorrected: () => void }) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState('');
  const [tag, setTag] = useState<CorrectionTagT | null>(null);

  const submit = useMutation({
    mutationFn: () =>
      api.submitCorrection({
        claimPath: props.claim.path,
        originalText: props.claim.text,
        userCorrectionText: text.trim() || undefined,
        tag: tag ?? undefined,
      }),
    onSuccess: () => {
      setEditing(false);
      setText('');
      setTag(null);
      props.onCorrected();
    },
  });

  const corrected = props.claim.correctedBy;

  return (
    <li className="group py-4 border-b border-border last:border-b-0">
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <p
            className={
              corrected
                ? 'line-through text-muted-foreground text-[15px] leading-relaxed'
                : 'text-[15px] leading-relaxed'
            }
          >
            {props.claim.text}
          </p>
          {corrected && (
            <div className="mt-2 pl-3 border-l-2 border-[var(--color-accent)]">
              <p className="text-[15px] leading-relaxed">
                {corrected.userText || (
                  <em className="text-muted-foreground">(flagged, no rewrite)</em>
                )}
              </p>
              {corrected.tag && (
                <span className="mt-1 inline-block text-[10px] uppercase tracking-[0.1em] text-muted-foreground">
                  {corrected.tag.replace(/_/g, ' ')}
                </span>
              )}
            </div>
          )}
        </div>
        {!corrected && !editing && (
          <button
            onClick={() => setEditing(true)}
            className="text-[12px] text-muted-foreground hover:text-foreground transition-colors opacity-0 group-hover:opacity-100 focus-visible:opacity-100 shrink-0 underline-offset-2 hover:underline"
          >
            doesn&apos;t sound like me
          </button>
        )}
      </div>

      {editing && (
        <div className="mt-4 rounded-[var(--radius)] bg-muted/60 border border-border p-4 animate-rise">
          <Textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="what would you say instead? (optional)"
            rows={2}
          />
          <div className="mt-3 flex flex-wrap gap-1.5">
            {TAGS.map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => setTag(tag === t.id ? null : t.id)}
                className={`text-[11px] px-2 py-1 rounded-full border transition-colors ${
                  tag === t.id
                    ? 'border-foreground bg-foreground text-background'
                    : 'border-border text-muted-foreground hover:text-foreground'
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
          <div className="mt-4 flex gap-2">
            <Button size="sm" disabled={submit.isPending} onClick={() => submit.mutate()}>
              {submit.isPending ? 'saving…' : 'Save correction'}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setEditing(false)}>
              Cancel
            </Button>
          </div>
        </div>
      )}
    </li>
  );
}

function ProfilePage() {
  const qc = useQueryClient();
  const profile = useQuery<ProfileResponse>({ queryKey: ['profile'], queryFn: api.profile });

  useEffect(() => {
    api.recordEvent({ kind: 'profile_open' }).catch(() => {});
  }, []);

  const regen = useMutation({
    mutationFn: api.regenerateProfile,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['profile'] }),
  });

  const onCorrected = () => qc.invalidateQueries({ queryKey: ['profile'] });

  if (profile.isLoading) {
    return (
      <Page>
        <p className="text-sm text-muted-foreground">loading…</p>
      </Page>
    );
  }
  const p = profile.data;
  if (!p) return null;

  const personalityEmpty = p.personality.length === 0 && p.themes.length === 0;
  const themesEmpty = p.fears.length === 0 && p.values.length === 0;
  const narrativeEmpty = !p.narrative;

  return (
    <Page>
      <PageHeader
        kicker="your portrait"
        title={
          p.version
            ? 'What the system thinks it knows.'
            : 'Nothing yet.'
        }
        subtitle="Hedged on purpose. Every line can be pushed back on, and the next regeneration honours every correction."
        actions={
          p.version && (
            <Badge variant="outline">
              v{p.version} · {p.entriesSeen} entries
            </Badge>
          )
        }
      />

      <Section
        title="who you seem to be"
        description="personality + cognitive style. hedged on purpose."
      >
        {personalityEmpty ? (
          <EmptyState
            title="Not enough yet."
            description="Capture a handful of entries first. The system regenerates the portrait every few entries."
          />
        ) : (
          <ul className="rounded-[var(--radius-lg)] border border-border bg-background px-5">
            {p.personality.map((c) => (
              <ClaimRow key={c.path} claim={c} onCorrected={onCorrected} />
            ))}
            {p.themes.map((c) => (
              <ClaimRow key={c.path} claim={c} onCorrected={onCorrected} />
            ))}
          </ul>
        )}
      </Section>

      <Section
        title="what you keep returning to"
        description="fears and values, in your words where possible."
      >
        {themesEmpty ? (
          <EmptyState title="Nothing has surfaced yet." />
        ) : (
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="rounded-[var(--radius-lg)] border border-border bg-background px-5">
              <div className="pt-4 pb-2 text-[11px] uppercase tracking-[0.12em] text-muted-foreground">
                fears
              </div>
              <ul>
                {p.fears.length > 0 ? (
                  p.fears.map((c) => (
                    <ClaimRow key={c.path} claim={c} onCorrected={onCorrected} />
                  ))
                ) : (
                  <li className="py-4 text-sm text-muted-foreground italic">none surfaced</li>
                )}
              </ul>
            </div>
            <div className="rounded-[var(--radius-lg)] border border-border bg-background px-5">
              <div className="pt-4 pb-2 text-[11px] uppercase tracking-[0.12em] text-muted-foreground">
                values
              </div>
              <ul>
                {p.values.length > 0 ? (
                  p.values.map((c) => (
                    <ClaimRow key={c.path} claim={c} onCorrected={onCorrected} />
                  ))
                ) : (
                  <li className="py-4 text-sm text-muted-foreground italic">none surfaced</li>
                )}
              </ul>
            </div>
          </div>
        )}
      </Section>

      <Section title="your story so far" description="written back to you. cap is 200 words.">
        {narrativeEmpty ? (
          <EmptyState title="Not enough to write a story yet." />
        ) : (
          p.narrative && (
            <article className="rounded-[var(--radius-lg)] border border-border bg-background px-5">
              <ul>
                <ClaimRow claim={p.narrative} onCorrected={onCorrected} />
              </ul>
            </article>
          )
        )}
      </Section>

      <Section
        title="how to talk to you"
        description="rules the coach follows, drawn from what you've said."
      >
        {p.directives.length === 0 ? (
          <EmptyState title="Not derived yet." />
        ) : (
          <ul className="rounded-[var(--radius-lg)] border border-border bg-background px-5">
            {p.directives.map((c) => (
              <ClaimRow key={c.path} claim={c} onCorrected={onCorrected} />
            ))}
          </ul>
        )}
      </Section>

      <div className="mt-10 flex items-center gap-3 border-t border-border pt-6">
        <Button
          variant="secondary"
          size="sm"
          disabled={regen.isPending}
          onClick={() => regen.mutate()}
        >
          {regen.isPending ? 'regenerating…' : 'Regenerate now'}
        </Button>
        <span className="text-xs text-muted-foreground">
          standing corrections get integrated on the next regen.
        </span>
      </div>
    </Page>
  );
}
